import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Image, X } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGE_KB = 500;

function QuestionRow({ q, idx, onChange, onRemove }) {
  const fileRef = useRef(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_KB * 1024) {
      toast.error(`Image must be under ${MAX_IMAGE_KB}KB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => onChange({ ...q, question_image: ev.target.result });
    reader.readAsDataURL(file);
  };

  const updateOption = (optIdx, value) => {
    const newOptions = [...(q.options || ["", "", "", ""])];
    newOptions[optIdx] = value;
    onChange({ ...q, options: newOptions });
  };

  const options = q.options || ["", "", "", ""];

  return (
    <Card className="p-4 border border-slate-200 bg-white space-y-4">
      {/* Row header */}
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-700 text-sm">Q{q.question_number}</span>
        <button
          onClick={onRemove}
          className="text-slate-400 hover:text-red-500 transition"
          data-testid={`remove-question-${idx}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Question text */}
      <div>
        <Label className="text-xs font-semibold text-slate-600 mb-1 block">Question Text</Label>
        <textarea
          rows={2}
          value={q.question_text}
          onChange={(e) => onChange({ ...q, question_text: e.target.value })}
          placeholder="Type your question here..."
          className="w-full border border-slate-200 rounded-lg p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-sky-300"
          data-testid={`question-text-${idx}`}
        />
      </div>

      {/* Image upload */}
      <div>
        {q.question_image ? (
          <div className="relative inline-block">
            <img
              src={q.question_image}
              alt="Question"
              className="max-h-40 rounded-lg border border-slate-200 object-contain"
            />
            <button
              onClick={() => onChange({ ...q, question_image: null })}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-sky-600 border border-dashed border-slate-300 hover:border-sky-300 rounded-lg px-3 py-2 transition"
          >
            <Image className="w-3.5 h-3.5" /> Add image (optional, max {MAX_IMAGE_KB}KB)
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
      </div>

      {/* Answer type + marks row */}
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Answer Type</Label>
          <div className="flex gap-2">
            {["MCQ", "TITA"].map((type) => (
              <button
                key={type}
                onClick={() => onChange({ ...q, answer_type: type })}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                  q.answer_type === type
                    ? "bg-sky-600 text-white border-sky-600"
                    : "bg-white text-slate-600 border-slate-300 hover:border-sky-300"
                }`}
                data-testid={`answer-type-${type}-${idx}`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Marks</Label>
          <input
            type="number"
            min={1}
            max={10}
            value={q.marks}
            onChange={(e) => onChange({ ...q, marks: parseInt(e.target.value, 10) || 1 })}
            className="w-16 border border-slate-200 rounded-lg p-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-sky-300"
            data-testid={`marks-input-${idx}`}
          />
        </div>
      </div>

      {/* MCQ Options */}
      {q.answer_type === "MCQ" && (
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-slate-600 block">Options (tick the correct one)</Label>
          {options.map((opt, optIdx) => (
            <div key={optIdx} className="flex items-center gap-2">
              <input
                type="radio"
                name={`correct-${q.question_number}`}
                checked={q.correct_answer === opt && opt !== ""}
                onChange={() => opt && onChange({ ...q, correct_answer: opt })}
                className="accent-emerald-600 w-4 h-4 flex-shrink-0"
                data-testid={`option-radio-${idx}-${optIdx}`}
              />
              <span className="text-xs font-bold text-slate-500 w-5">{String.fromCharCode(65 + optIdx)}.</span>
              <input
                type="text"
                value={opt}
                onChange={(e) => {
                  updateOption(optIdx, e.target.value);
                  // Update correct_answer if it was this option
                  if (q.correct_answer === options[optIdx]) {
                    onChange({ ...q, options: options.map((o, i) => i === optIdx ? e.target.value : o), correct_answer: e.target.value });
                  }
                }}
                placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                className="flex-1 border border-slate-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                data-testid={`option-text-${idx}-${optIdx}`}
              />
            </div>
          ))}
          {!q.correct_answer && (
            <p className="text-xs text-amber-600">Select the correct option by clicking the radio button.</p>
          )}
        </div>
      )}

      {/* TITA */}
      {q.answer_type === "TITA" && (
        <div className="space-y-2">
          <Label className="text-xs font-semibold text-slate-600 block">Correct Answer (optional — for your reference)</Label>
          <input
            type="text"
            value={q.correct_answer || ""}
            onChange={(e) => onChange({ ...q, correct_answer: e.target.value })}
            placeholder="Enter expected answer (optional)"
            className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
            data-testid={`tita-answer-${idx}`}
          />
          <p className="text-xs text-slate-500 italic">Student types their answer. You review and award marks later.</p>
        </div>
      )}
    </Card>
  );
}

export default function ManualTestBuilder({ questions, onChange, testMeta }) {
  const newQuestion = (number) => ({
    question_number: number,
    question_text: "",
    question_image: null,
    answer_type: "MCQ",
    options: ["", "", "", ""],
    correct_answer: "",
    marks: 1,
  });

  const addAfter = (idx) => {
    const updated = [...questions];
    const newQ = newQuestion(0);
    updated.splice(idx + 1, 0, newQ);
    // Renumber
    const renumbered = updated.map((q, i) => ({ ...q, question_number: i + 1 }));
    onChange(renumbered);
  };

  const remove = (idx) => {
    if (questions.length <= 1) {
      toast.error("A test must have at least one question");
      return;
    }
    const updated = questions.filter((_, i) => i !== idx);
    const renumbered = updated.map((q, i) => ({ ...q, question_number: i + 1 }));
    onChange(renumbered);
  };

  const update = (idx, updated) => {
    const newQs = questions.map((q, i) => (i === idx ? updated : q));
    onChange(newQs);
  };

  return (
    <div className="space-y-4">
      {/* Test meta header */}
      <div className="bg-slate-50 rounded-xl p-4 text-sm flex flex-wrap gap-x-6 gap-y-1 text-slate-700">
        <span><strong>Grade:</strong> {testMeta.grade}</span>
        <span><strong>Subject:</strong> {testMeta.subject}</span>
        <span><strong>Chapter:</strong> {testMeta.chapter}</span>
        {testMeta.learnerName && <span><strong>Student:</strong> {testMeta.learnerName}</span>}
        <span className="ml-auto font-semibold text-sky-700">
          {questions.length} question{questions.length !== 1 ? "s" : ""} &nbsp;·&nbsp;
          {questions.reduce((s, q) => s + (q.marks || 1), 0)} total marks
        </span>
      </div>

      {/* Question rows */}
      {questions.map((q, idx) => (
        <div key={idx}>
          <QuestionRow
            q={q}
            idx={idx}
            onChange={(updated) => update(idx, updated)}
            onRemove={() => remove(idx)}
          />
          {/* Add after button */}
          <div className="flex justify-center mt-2">
            <button
              onClick={() => addAfter(idx)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-sky-600 border border-dashed border-slate-300 hover:border-sky-300 rounded-full px-4 py-1.5 transition"
              data-testid={`add-after-${idx}`}
            >
              <Plus className="w-3.5 h-3.5" /> Add question
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
